/**
 * Utilidades para la vista de registro de servicios públicos en formato
 * matriz mensual (días del mes x servicios).
 *
 * ------------------------------------------------------------------
 * IMPORTANTE — desfase de fecha del backend
 * ------------------------------------------------------------------
 * El endpoint `POST /utility-services/create` resta un día a la fecha
 * recibida antes de guardarla:
 *
 *   body.fecha = new Date(body.fecha);
 *   body.fecha.setDate(body.fecha.getDate() - 1);
 *
 * Por eso, para que un registro quede almacenado en el día D hay que
 * enviar D + 1 (ver `buildCreateDateIso`). El endpoint de update NO
 * aplica ese desfase, así que ahí se envía la fecha real del día
 * (ver `buildUpdateDateIso`).
 *
 * Todas las fechas se manejan al mediodía UTC para que la conversión a
 * la zona horaria local (o del servidor) nunca cambie el día.
 */

export const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const WEEKDAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/** Mediodía UTC del día indicado. `month` es 0-based. */
export const noonUtc = (year, month, day) =>
  new Date(Date.UTC(year, month, day, 12, 0, 0));

/** Clave estable `YYYY-MM-DD` a partir de año/mes(0-based)/día. */
export const buildDayKey = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/**
 * Clave `YYYY-MM-DD` de un registro almacenado. Se leen las partes UTC
 * porque los registros se guardan al mediodía UTC.
 */
export const getRecordDayKey = (record) => {
  if (!record?.fecha) return null;
  const date = new Date(record.fecha);
  if (Number.isNaN(date.getTime())) return null;
  return buildDayKey(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/** Fecha a enviar al crear: día + 1, porque el backend resta un día. */
export const buildCreateDateIso = (year, month, day) =>
  noonUtc(year, month, day + 1).toISOString();

/** Fecha a enviar al actualizar: el día real (update no aplica desfase). */
export const buildUpdateDateIso = (year, month, day) =>
  noonUtc(year, month, day).toISOString();

/**
 * Rango `$gte`/`$lte` que cubre el mes completo.
 *
 * Va de día UTC a día UTC, no de mediodía a mediodía: los registros
 * históricos no comparten una sola hora de almacenamiento (hay lecturas
 * guardadas a las 05:00Z y otras a las 12:00Z). Acotar el rango al
 * mediodía dejaría el día 1 de los meses antiguos fuera del mes, y peor
 * aún, lo haría aparecer como "registro anterior" al resolver la base.
 */
export const buildMonthRange = (year, month) => {
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return {
    $gte: new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)).toISOString(),
    $lte: new Date(Date.UTC(year, month, daysInMonth, 23, 59, 59, 999)).toISOString(),
  };
};

/**
 * Inicio del mes, usado como corte `$lt` al buscar la lectura base.
 * Coincide con el `$gte` de `buildMonthRange` para que ambos rangos sean
 * complementarios: ningún registro queda fuera ni contado dos veces.
 */
export const buildMonthStartIso = (year, month) =>
  new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)).toISOString();

/**
 * Descriptor de cada día del mes: número, clave, etiqueta de día de la
 * semana y banderas de fin de semana / hoy / futuro.
 */
export const buildMonthDays = (year, month) => {
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const now = new Date();
  const todayKey = buildDayKey(now.getFullYear(), now.getMonth(), now.getDate());

  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = index + 1;
    const key = buildDayKey(year, month, day);
    const weekday = noonUtc(year, month, day).getUTCDay();
    return {
      day,
      key,
      weekday,
      weekdayLabel: WEEKDAY_NAMES[weekday],
      isWeekend: weekday === 0 || weekday === 6,
      isToday: key === todayKey,
      isFuture: key > todayKey,
    };
  });
};

/**
 * Factor de conversión a litros. Replica la lógica de la vista clásica:
 * si el servicio no trae `factorConversion` (o trae 1) se infiere a
 * partir del tipo y la unidad.
 */
export const resolveLitersFactor = (service) => {
  const configured = parseFloat(service?.factorConversion);
  if (configured && configured !== 1) return configured;

  const tipo = String(service?.tipo || '').toLowerCase();
  const unidad = String(service?.unidad || '').toLowerCase();
  const isWater = tipo === 'agua' || tipo === 'agua caliente';

  if (isWater && unidad === 'm3') return 1000;
  if (isWater && unidad === 'galones') return 3.78541;

  return configured || 1;
};

/** Consumo = lectura nueva - lectura anterior (nunca negativo). */
export const computeConsumo = (previous, current) => {
  const prev = Number(previous);
  const curr = Number(current);
  if (!Number.isFinite(prev) || !Number.isFinite(curr)) return 0;
  return Math.max(0, curr - prev);
};

export const computeLitros = (consumo, service) =>
  consumo * resolveLitersFactor(service);

export const computeCosto = (consumo, unitPrice) =>
  consumo * (parseFloat(unitPrice) || 0);

/**
 * Precio unitario a aplicar a una celda.
 *
 * Para un registro ya guardado se deriva su propia tarifa (costo / consumo).
 * `servicio.precio` es la tarifa VIGENTE HOY y pudo cambiar desde entonces:
 * recalcular un mes viejo con el precio nuevo reescribiría el costo histórico
 * de un periodo ya cerrado. Sólo las lecturas nuevas usan el precio actual.
 */
export const resolveUnitPrice = (record, service) => {
  const consumo = Number(record?.consumo);
  const costo = Number(record?.costo);
  if (Number.isFinite(consumo) && consumo > 0 && Number.isFinite(costo)) {
    return costo / consumo;
  }
  return parseFloat(service?.precio) || 0;
};

/** Convierte una entrada de texto a número, o `null` si está vacía. */
export const parseReading = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim().replace(',', '.');
  if (raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const numberFormatter = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 });
const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
});
const unitPriceFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 3,
});

export const formatNumber = (value) =>
  Number.isFinite(Number(value)) ? numberFormatter.format(Number(value)) : '-';

export const formatCurrency = (value) =>
  Number.isFinite(Number(value)) ? currencyFormatter.format(Number(value)) : '-';

/**
 * Precio unitario. A diferencia de un total, aquí los decimales importan:
 * hay servicios tarifados a 2,567 y a 3 por unidad, y redondeados a pesos
 * enteros se verían idénticos en la cabecera de la planilla.
 */
export const formatUnitPrice = (value) =>
  Number.isFinite(Number(value)) ? unitPriceFormatter.format(Number(value)) : '-';

/** Compara dos números tolerando el redondeo de coma flotante. */
export const isSameNumber = (a, b, epsilon = 0.0001) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return na === nb;
  return Math.abs(na - nb) < epsilon;
};
